import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useAdminTheme } from '../../context/AdminThemeContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, Printer, Copy, Calendar, User, Phone, CreditCard } from 'lucide-react';

const sampleData = {
  id: 4,
  transaction_code: 'PB004',
  name: 'Mrs. Bette Bahringer',
  contact_number: '+639399142499',
  payment_type: 'counter',
  payment_status: 'paid',
  total_amount: '600.00',
  created_at: '2026-01-12T00:45:38.000000Z',
  booking_infos: [
    {
      id: 9,
      court_no: 1,
      player_count: 2,
      booking_date: '2026-02-09',
      booking_time_start: '19:00:00',
      booking_time_end: '20:00:00'
    }
  ]
};

const AdminSuccessPaymentPage = () => {
  const [payment, setPayment] = useState(null);
  const { isDarkMode } = useAdminTheme();

  useEffect(() => {
    setPayment(sampleData);
  }, []);

  if (!payment) return null;

  // Helper to format 24h time to 12h
  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <AdminLayout>
      <div className="min-h-screen p-4 md:p-8 flex flex-col items-center">
        
        {/* Success Header Animation Area */}
        <div className="text-center mb-8 animate-in fade-in zoom-in duration-500">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Payment Received</h1>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Transaction completed successfully</p>
        </div>

        {/* Main Receipt Card */}
        <div className="w-full max-w-2xl relative">
          {/* Top Notch decorative element */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-green-500 rounded-b-full shadow-[0_0_15px_rgba(34,197,94,0.5)]"></div>

          <Card className={`border-none shadow-2xl overflow-hidden rounded-2xl ${isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'}`}>
            <CardContent className="p-0">
              
              {/* Top Section: Amount & ID */}
              <div className={`p-8 text-center border-b border-dashed ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                <p className={`text-sm uppercase tracking-widest font-semibold mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Paid</p>
                <h2 className={`text-5xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>₱{payment.total_amount}</h2>
                <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono ${isDarkMode ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                  {payment.transaction_code}
                  <button className="hover:text-blue-600 transition-colors"><Copy size={12}/></button>
                </div>
              </div>

              {/* Middle Section: Customer Details */}
              <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                <section className="space-y-4">
                  <h3 className={`text-xs uppercase font-bold flex items-center gap-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>
                    <User size={14} /> Customer Details
                  </h3>
                  <div>
                    <p className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{payment.name}</p>
                    <p className={`flex items-center gap-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <Phone size={14} /> {payment.contact_number}
                    </p>
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className={`text-xs uppercase font-bold flex items-center gap-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>
                    <CreditCard size={14} /> Payment Info
                  </h3>
                  <div>
                    <p className={`font-medium capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{payment.payment_type} Payment</p>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>{new Date(payment.created_at).toLocaleDateString()} • {new Date(payment.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                  </div>
                </section>
              </div>

              {/* Bottom Section: Booking Highlights */}
              <div className={`mx-8 p-6 rounded-xl mb-8 ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-slate-900 text-white'}`}>
                <h3 className="text-xs uppercase font-bold text-slate-500 mb-4 flex items-center gap-2">
                  <Calendar size={14} /> Booking Schedule
                </h3>
                {payment.booking_infos.map((booking) => (
                  <div key={booking.id} className="flex justify-between items-center">
                    <div>
                      <p className="text-lg font-semibold">Court {booking.court_no}</p>
                      <p className="text-slate-400 text-sm">{new Date(booking.booking_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-mono text-lg">
                        {formatTime(booking.booking_time_start)}
                      </p>
                      <p className="text-slate-500 text-xs">For {booking.player_count} Players</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="p-8 pt-0 flex flex-col sm:flex-row gap-3">
                <Button className={`flex-1 py-6 rounded-xl font-bold transition-all active:scale-95 ${isDarkMode ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
                  <Printer className="mr-2 h-4 w-4" /> Print Receipt
                </Button>
                <Button variant="outline" className={`flex-1 py-6 rounded-xl font-bold transition-all active:scale-95 ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-200 hover:bg-slate-50'}`}>
                  Download PDF
                </Button>
              </div>

            </CardContent>
          </Card>
          
          {/* Decorative Footer "Teeth" for the receipt */}
          <div className="flex justify-between px-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="w-4 h-4 bg-slate-900/10 rounded-full -mt-2"></div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSuccessPaymentPage;